/* Lego player routing gate (2026-08-27, corrected 2026-09-01).

   The lego player is the LIGHTER player — its whole point is to run well on
   low-end devices (webOS 3/4, Chromium < 60, <=2 cores, <2 GB RAM). So the
   gate defaults it ON for that population; we do NOT exclude low-end (the
   earlier `&& !isLowEndDevice()` was inverted). Non-low-end devices only get it
   when explicitly forced, so the lego can be exercised on a desktop browser or
   emulator without editing this file.

   The single chokepoint is `shouldUseLegoPlayer()`, a hook-free pure function so
   it can run inside the router (`history.js`) and the route-level deep-link
   redirect (`routes/Private/Player/Player.jsx`) at navigation time — before the
   player mounts and before DeviceInfo is necessarily available.

   Decision order:
     1. master switch VITE_LEGO_PLAYER_ACTIVE (rollout flag) — off everywhere
        unless a build enables it
     2. low-end device (the lego's own VITE_IS_LOW_END_DEVICE_LEGO rule) AND,
        for live, the channel is in VITE_LEGO_PLAYER_CHANNELS -> use the lego
        (ON + low-end + channel = lego; ON + low-end + channel not listed =
        classic player)
     3. otherwise -> use the lego only if explicitly forced, either:
          - localStorage 'forceLegoPlayer' === 'true' (runtime, no rebuild —
            set in the browser devtools: localStorage.setItem('forceLegoPlayer','true'))
          - VITE_LEGO_PLAYER_FORCE_ACTIVE (build-time, e.g. `.env.local`)

   Low-end detection is the shared `isLowEndDevice` from
   `~/utils/platform/isLowEndDevice` — the same rule the UI and both players'
   tier configs use (OS + version, Chromium < 60, weak hardware). Called here
   without deviceInfo, it applies the OS-version rule with the last sdkVersion
   the app reported; webOS 3/4 resolve through the UA floor regardless. */

import { IS_LEGO_PLAYER_ACTIVE, IS_LEGO_PLAYER_FORCE_ACTIVE, LEGO_PLAYER_CHANNELS } from './env';
import { valuesAllChannelsCatchup } from './constants';
import { isLowEndDeviceLego } from './platform/isLowEndDevice';

/* Per-channel rollout (VITE_LEGO_PLAYER_CHANNELS, `allChannels` = every one).
   Only LIVE playback is channel-gated; VOD has no channel and follows the
   low-end rule alone. */
function isLegoChannelAllowed(channelId) {
  return LEGO_PLAYER_CHANNELS.has(valuesAllChannelsCatchup)
    || LEGO_PLAYER_CHANNELS.has(channelId);
}

function isLegoPlayerForced() {
  try {
    if (localStorage.getItem('forceLegoPlayer') === 'true') return true;
  } catch {
    /* localStorage unavailable (SSR/restricted) — fall through to the env flag. */
  }
  return IS_LEGO_PLAYER_FORCE_ACTIVE;
}

/**
 * Whether the Solid lego player should be used for this playback.
 *
 * @param {{ isLive?: boolean, channelId?: string }} [playback] - live playback
 *   passes its raw channel id; VOD (or an unknown target) omits `isLive`.
 * @returns {boolean} true when the lego flag is on AND (the device is low-end
 *   for the lego AND, on live, the channel is listed — OR an explicit force
 *   override is set).
 */
export function shouldUseLegoPlayer({ isLive = false, channelId } = {}) {
  if (!IS_LEGO_PLAYER_ACTIVE) return false;
  if (isLowEndDeviceLego() && (!isLive || isLegoChannelAllowed(channelId))) return true;
  return isLegoPlayerForced();
}
